// import components
import { IconButton } from '@mui/material';
import { ContextMenu } from 'components/mrp/components/ContextMenu';

// import external libraries
import { useTranslation } from 'react-i18next';

// import utils
import {
  navigateToCareReceiverPrescriptions,
  navigateToEntityCalendar
} from '../../Backlog.utils';

// import styles
import { EntityActionsIcon } from './BacklogEntityActionsMenu.styles';

// import types
import type { IconType } from '@sdb/planning-ui/ui-3/packages/icons/__generated__';
import type { ContextMenuAction } from 'components/mrp/components/ContextMenu/ContextMenu.types';
import type { BacklogEntityActionsMenuProps } from './BacklogEntityActionsMenu.types';

/**
 * Where a planner leaves the takenlijst for one entity: their prescriptions, or their agenda in
 * afsprakenplanning. The same kebab afsprakenplanning opens from its own calendar headers, so the menu
 * looks and behaves identically in both places.
 *
 * Shared by the weeks grid and the week detail — both show the same entity band, and a planner who found
 * the menu in one should find it in the other.
 */
const BacklogEntityActionsMenu = ({
  entityId,
  entityName,
  entityType
}: BacklogEntityActionsMenuProps) => {
  'use memo';

  const { t } = useTranslation('common', { keyPrefix: 'backlog' });

  const actions: ContextMenuAction[] = [
    // Prescriptions only make sense for a care receiver — an employee, resource or workplace has no
    // prescriptions page to go to.
    ...(entityType === 'CLIENT'
      ? [
          {
            value: t('goToPrescriptions'),
            icon: { iconType: 'table-cells' as IconType },
            onClick: () =>
              navigateToCareReceiverPrescriptions(entityId, entityName)
          }
        ]
      : []),
    {
      value: t('goToCalendar'),
      icon: { iconType: 'calendar' as IconType },
      onClick: () => navigateToEntityCalendar(entityId, entityName, entityType)
    }
  ];

  return (
    <ContextMenu id={`backlog-entity-actions-${entityId}`} actions={actions}>
      <IconButton
        size="small"
        aria-label={t('entityActions', { name: entityName })}
      >
        <EntityActionsIcon type="ellipsis-stroke-vertical" color="action" />
      </IconButton>
    </ContextMenu>
  );
};

export default BacklogEntityActionsMenu;
